import { KeyValue } from "../types";

export interface VariableScope {
  name: string;
  variables: KeyValue[];
}

/**
 * Resolves variables in a string using the {{variable}} syntax.
 * Priority is determined by the order of scopes (first scope has highest priority).
 */
export function resolveVariables(input: string, scopes: VariableScope[]): string {
  if (!input || !scopes || scopes.length === 0) return input;

  // Flatten variables into a single map based on priority
  // We iterate backwards so higher priority scopes (earlier in the array) overwrite lower ones
  const resolvedMap: Record<string, string> = {};
  [...scopes].reverse().forEach(scope => {
    scope.variables.forEach(v => {
      if (v.enabled && v.key) {
        resolvedMap[v.key] = v.value;
      }
    });
  });

  let resolved = input;
  const variableRegex = /{{([^}]+)}}/g;
  
  // Replace variables using the map
  resolved = resolved.replace(variableRegex, (match, key) => {
    return resolvedMap[key] !== undefined ? resolvedMap[key] : match;
  });

  return resolved;
}

/**
 * Finds all variables in a string that are NOT defined in any provided scope.
 */
export function findMissingVariables(input: string, scopes: VariableScope[]): string[] {
  if (!input) return [];
  
  const variableRegex = /{{([^}]+)}}/g;
  const matches = input.matchAll(variableRegex);
  const missing: string[] = [];
  
  const allKeys = new Set<string>();
  scopes.forEach(scope => {
    scope.variables.forEach(v => {
      if (v.enabled && v.key) {
        allKeys.add(v.key);
      }
    });
  });
  
  for (const match of matches) {
    const key = match[1];
    if (!allKeys.has(key)) {
      missing.push(key);
    }
  }
  
  return [...new Set(missing)];
}

/**
 * Gets the source scope of a variable.
 */
export function getVariableSource(key: string, scopes: VariableScope[]): string | null {
  for (const scope of scopes) {
    const found = scope.variables.find(v => v.key === key && v.enabled);
    if (found) return scope.name;
  }
  return null;
}
